import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";

import { ActionButtonIntent, HeaderDivider, TriggerButton, TriggerButtonIntent } from "widgets";

import { type ISelectionModel } from "../../app-utils/selection-model.js";
import { pluralizeWord } from "../../app-utils/string.js";
import { type Attribute, type InputTableQuerySpec } from "../../graphql.js";
import { deleteAttributes } from "../../redux/modules/sample-admin.js";
import {
  type ITableColumn,
  type ITableData,
  createUniqueKeyFromTableSpec,
  getTableDataSlice,
  setOrderedColumnList,
  toggleSelection,
} from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import ColumnSelectionButton from "../core/ColumnSelectionButton.js";
import { ConfirmationDialog } from "../core/overlays/Dialogs.js";
import { ModalActionWidget, ModalControlledOverlay } from "../core/overlays/index.js";
import AddSampleAttributeDialog from "./AddSampleAttributeDialog.js";

interface ISampleAttributesAdminToolbarOwnProps {
  tableSpec: InputTableQuerySpec;
  attributeCreated: (createdAttribute: Attribute) => void;
  reloadAttributeRows: () => Promise<any>;
  renderOffScreen?: boolean; // Only useful for off-screen rendering
}

interface ISampleAttributesAdminToolbarAppStateProps {
  // Redux injected props
  selectionModel: ISelectionModel;
  attributesTable: ITableData;
}

interface ISampleAttributesAdminToolbarDispatchProps {
  setColumnList: (columns: ITableColumn[], tableSpec: InputTableQuerySpec) => Promise<boolean>;
  deleteAttributes: (ids: number[]) => Promise<boolean>;
  clearSelection: () => void;
}

export interface ISampleAttributesAdminToolbarProps
  extends ISampleAttributesAdminToolbarAppStateProps,
    ISampleAttributesAdminToolbarDispatchProps,
    ISampleAttributesAdminToolbarOwnProps {}

function mapStateToProps(
  state: IAppState,
  ownProps: ISampleAttributesAdminToolbarOwnProps,
): ISampleAttributesAdminToolbarAppStateProps {
  const tableData: ITableData = getTableDataSlice(state.tableData, ownProps.tableSpec);

  return {
    selectionModel: tableData && tableData.selectionModel,
    attributesTable: tableData,
  };
}

function mapDispatchToProps(
  dispatch,
  ownProps: ISampleAttributesAdminToolbarOwnProps,
): ISampleAttributesAdminToolbarDispatchProps {
  return {
    setColumnList: (columns: ITableColumn[], tableSpec: InputTableQuerySpec) =>
      dispatch(setOrderedColumnList(columns, tableSpec)),
    deleteAttributes: (ids: number[]) => dispatch(deleteAttributes(ids)),
    clearSelection: () => dispatch(toggleSelection(false, ownProps.tableSpec)),
  };
}

interface ISampleAttributesAdminToolbarState {
  showAddSampleAttributeDialog: boolean;
}

class SampleAttributesAdminToolbar extends PureComponent<
  ISampleAttributesAdminToolbarProps,
  ISampleAttributesAdminToolbarState
> {
  constructor(props: ISampleAttributesAdminToolbarProps) {
    super(props);

    this.state = {
      showAddSampleAttributeDialog: false,
    };
  }

  getDeleteButtonDisabledTooltip = () => {
    if (!this.props.selectionModel || this.props.selectionModel.selectedIds.length === 0) {
      return "Please make a selection to delete an attribute";
    } else if (
      this.props.selectionModel.selectedIds.find((selectedId: number) => {
        const selectedAttribute: Attribute = (this.props.attributesTable.pageRows as Attribute[]).find(
          (attribute: Attribute) => attribute.id === selectedId,
        );
        return selectedAttribute && !!selectedAttribute.representedColumn;
      })
    ) {
      return "An attribute cannot be deleted if it is a represented attribute";
    } else if (
      this.props.selectionModel.selectedIds.find((selectedId: number) => {
        const selectedAttribute: Attribute = (this.props.attributesTable.pageRows as Attribute[]).find(
          (attribute: Attribute) => attribute.id === selectedId,
        );
        return selectedAttribute && selectedAttribute.hasData;
      })
    ) {
      return "An attribute cannot be deleted if it already has data";
    }
  };

  handleDelete = (): Promise<any> => {
    if (
      !this.props.attributesTable.isLoading &&
      this.props.attributesTable.selectionModel &&
      this.props.attributesTable.selectionModel.selectedIds.length > 0
    ) {
      return this.props
        .deleteAttributes(this.props.selectionModel.selectedIds as number[])
        .then((_response: boolean) => {
          this.props.clearSelection();
          this.props.reloadAttributeRows();
        });
    } else {
      return Promise.resolve();
    }
  };

  render(): ReactElement {
    const selectionCount: number = (this.props.selectionModel && this.props.selectionModel.selectedIds.length) || 0;
    const deleteButtonDisabledTooltip: string = this.getDeleteButtonDisabledTooltip();

    return (
      <>
        <ColumnSelectionButton portalContext={createUniqueKeyFromTableSpec(this.props.tableSpec)} />
        <HeaderDivider />
        <ModalActionWidget
          actionWidget={({ open }) => (
            <TriggerButton
              id={"sample-attrib-admin-delete"}
              tooltip={"Delete the selected sample attribute(s)"}
              onPress={() => open()}
              isDisabled={deleteButtonDisabledTooltip}
              intent={TriggerButtonIntent.Danger}
              icon={"delete"}
              // renderOffScreen={this.props.renderOffScreen}
              label={"Delete"}
              showLabel
            />
          )}
        >
          {({ close }) => (
            <ConfirmationDialog
              headerProps={{
                title: `Delete ${pluralizeWord("Attribute", this.props.selectionModel.selectedIds.length)}`,
              }}
              mainActionButtonProps={{
                children: `Delete`,
                onPress: () => this.handleDelete().then(() => close()),
                intent: ActionButtonIntent.Danger,
              }}
              closeFn={close}
            >
              Are you sure you want to delete {selectionCount}{" "}
              <span style={{ fontWeight: 600 }}>selected {pluralizeWord("attribute", selectionCount)}</span>?
            </ConfirmationDialog>
          )}
        </ModalActionWidget>
        <HeaderDivider />

        <TriggerButton
          key={"sample-type-admin-add"}
          icon={"add"}
          tooltip={"Create a new sample attribute"}
          onPress={() => this.setState({ showAddSampleAttributeDialog: !this.state.showAddSampleAttributeDialog })}
          label={"New Attribute"}
          showLabel
        />

        {this.state.showAddSampleAttributeDialog && this.props.renderOffScreen !== true && (
          <ModalControlledOverlay close={() => this.setState({ showAddSampleAttributeDialog: false })} isOpen={true}>
            {({ close }) => <AddSampleAttributeDialog attributeCreated={this.props.attributeCreated} onClose={close} />}
          </ModalControlledOverlay>
        )}
      </>
    );
  }
}

export default connect<
  ISampleAttributesAdminToolbarAppStateProps,
  ISampleAttributesAdminToolbarDispatchProps,
  ISampleAttributesAdminToolbarOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(SampleAttributesAdminToolbar);
